import ArrowCircleDown from '@mui/icons-material/ArrowCircleDown';
import CancelOutlined from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined';
import RestoreOutlined from '@mui/icons-material/RestoreOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { JobStateEvent } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { partition } from 'lodash-es';
import { ReactNode, useCallback, useRef, useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import JobStatusChip from '~components/Job/JobStatusChip';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import {
  LoadingSpinner,
  OverflowAwareText,
} from '~components/Order/ordersDispatchStyledComponents';
import { Job } from '~hooks/useJob/models';

const colorByJobStateEvent: Partial<
  Record<JobStateEvent, keyof Theme['brandV2']['colors']>
> = {
  [JobStateEvent.ACCEPT]: 'treadGreen',
  [JobStateEvent.ARRIVE]: 'treadBlue',
  [JobStateEvent.CANCEL]: 'treadRed',
  [JobStateEvent.COMPLETE]: 'treadGreen',
  [JobStateEvent.ENROUTE]: 'treadBlue',
  [JobStateEvent.LOAD_MATERIAL]: 'treadBlue',
  [JobStateEvent.REJECT]: 'treadRed',
  [JobStateEvent.REQUEST]: 'treadYellowDark',
  [JobStateEvent.UNASSIGN]: 'treadGray3',
  [JobStateEvent.UNLOAD_MATERIAL]: 'treadBlue',
  [JobStateEvent.RESTORE]: 'treadGreen',
} as const;

interface JobStatusTransitionerProps {
  closeOnTransitionComplete?: boolean;
  isTransitioningToStatus?: JobStateEvent;
  job: Job;
  onTransitionToStatus: (state: JobStateEvent) => Promise<void>;
  transitionableStates: JobStateEvent[];
  isDisabled?: boolean;
}

export default function JobStatusTransitioner({
  closeOnTransitionComplete,
  isTransitioningToStatus,
  job,
  onTransitionToStatus,
  transitionableStates,
  isDisabled = false,
}: JobStatusTransitionerProps) {
  const theme = useTheme();
  const menuHandler = useRef<MenuHandler>(null);
  const isTransitioning = Boolean(isTransitioningToStatus);

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false);

  const [specialStates, otherStates] = partition(transitionableStates, (state) => {
    return [JobStateEvent.COMPLETE, JobStateEvent.CANCEL, JobStateEvent.RESTORE].includes(
      state,
    );
  });

  const isCompletable = specialStates.includes(JobStateEvent.COMPLETE);
  const isCancellable = specialStates.includes(JobStateEvent.CANCEL);
  const isRestorable = specialStates.includes(JobStateEvent.RESTORE);

  const handleMenuItemClick = useCallback(
    async (state: JobStateEvent) => {
      if (isTransitioning) return;

      if (state === JobStateEvent.UNASSIGN) {
        setIsConfirmationDialogOpen(true);
      } else if (state === JobStateEvent.RESTORE) {
        setIsRestoreDialogOpen(true);
      } else {
        try {
          await onTransitionToStatus(state);
        } finally {
          if (closeOnTransitionComplete) {
            menuHandler.current?.onClose?.();
          }
        }
      }
    },
    [isTransitioning, onTransitionToStatus, closeOnTransitionComplete],
  );

  if (transitionableStates.length === 0) {
    return <JobStatusChip jobState={job.status} hideDropdownArrow />;
  }

  return (
    <>
      <Menu
        ref={menuHandler}
        disableAutoFocusItem
        preventCloseCondition={isTransitioning}
        menuTrigger={
          <Box sx={{ cursor: 'pointer', display: 'inline-block' }}>
            <JobStatusChip jobState={job.status} />
          </Box>
        }
      >
        {otherStates.map((state) => (
          <JobStatusTransitionerMenuItem
            key={state}
            isDisabled={isTransitioning || isDisabled}
            isLoading={isTransitioningToStatus === state}
            onClick={() => handleMenuItemClick(state)}
            text={t('actions.move_to', { event: t(`actions.${state}`) })}
            dataUi={`page-dispatch__job-status-transitioner__job-${job.jobId}__${state}-button`}
            leftIcon={
              <ArrowCircleDown
                sx={{
                  color:
                    theme.brandV2.colors[colorByJobStateEvent[state] ?? 'treadGray3'],
                  fontSize: '15px',
                  transform: 'rotate(-90deg)',
                }}
              />
            }
          />
        ))}

        {(isCompletable || isCancellable) && otherStates.length > 0 && <Divider />}

        {isCompletable && (
          <JobStatusTransitionerMenuItem
            isDisabled={isTransitioning || isDisabled}
            isLoading={isTransitioningToStatus === JobStateEvent.COMPLETE}
            onClick={() => handleMenuItemClick(JobStateEvent.COMPLETE)}
            text={t('dispatch.job.mark_as_completed')}
            dataUi={`page-dispatch__job-status-transitioner__job-${job.jobId}__complete-button`}
            leftIcon={
              <CheckCircleOutlineOutlined
                sx={{
                  color: theme.brandV2.colors.treadGreen,
                  fontSize: '15px',
                }}
              />
            }
          />
        )}

        {isCancellable && (
          <JobStatusTransitionerMenuItem
            isDisabled={isTransitioning || isDisabled}
            isLoading={isTransitioningToStatus === JobStateEvent.CANCEL}
            onClick={() => handleMenuItemClick(JobStateEvent.CANCEL)}
            text={t('dispatch.job.cancel_job')}
            dataUi={`page-dispatch__job-status-transitioner__job-${job.jobId}__cancel-button`}
            leftIcon={
              <CancelOutlined
                sx={{
                  color: theme.brandV2.colors.treadRed,
                  fontSize: '15px',
                }}
              />
            }
          />
        )}

        {isRestorable && otherStates.length > 0 && <Divider />}

        {isRestorable && (
          <JobStatusTransitionerMenuItem
            isDisabled={isTransitioning || isDisabled}
            isLoading={isTransitioningToStatus === JobStateEvent.RESTORE}
            onClick={() => handleMenuItemClick(JobStateEvent.RESTORE)}
            text={t('dispatch.job.restore_confirm_title')}
            dataUi={`page-dispatch__job-status-transitioner__job-${job.jobId}__restore-button`}
            leftIcon={
              <RestoreOutlined
                sx={{
                  color: theme.brandV2.colors.treadGreen,
                  fontSize: '15px',
                }}
              />
            }
          />
        )}
      </Menu>

      <ConfirmationDialog
        isBusy={isTransitioning}
        isOpen={isConfirmationDialogOpen}
        onClose={() => setIsConfirmationDialogOpen(false)}
        onConfirm={async () => {
          await onTransitionToStatus(JobStateEvent.UNASSIGN);
          setIsConfirmationDialogOpen(false);
        }}
        title={t('actions.confirm_action')}
      >
        <Typography>
          {job.driver
            ? t('dispatch.drivers.confirm_driver_removal')
            : t('dispatch.drivers.confirm_vendor_removal')}
        </Typography>
      </ConfirmationDialog>

      <ConfirmationDialog
        isBusy={isTransitioning}
        isOpen={isRestoreDialogOpen}
        onClose={() => setIsRestoreDialogOpen(false)}
        onConfirm={async () => {
          await onTransitionToStatus(JobStateEvent.RESTORE);
          setIsRestoreDialogOpen(false);
        }}
        title={t('dispatch.job.restore_confirm_title')}
      >
        <Typography>{t('dispatch.job.restore_confirm_description')}</Typography>
      </ConfirmationDialog>
    </>
  );
}

interface JobStatusTransitionerMenuItemProps {
  isDisabled?: boolean;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  onClick: () => void;
  text: string;
  dataUi?: string;
}

function JobStatusTransitionerMenuItem({
  isDisabled,
  isLoading,
  leftIcon,
  onClick,
  text,
  dataUi,
}: JobStatusTransitionerMenuItemProps) {
  const theme = useTheme();

  return (
    <MenuItem
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation();

        if (!isDisabled) {
          onClick();
        }
      }}
      data-ui={dataUi || 'page-dispatch__job-status-transitioner__menu-item'}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 1,
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        {leftIcon}

        <OverflowAwareText
          flex={1}
          sx={{
            color: isDisabled ? theme.brandV2.colors.treadGray6 : undefined,
          }}
        >
          {text}
        </OverflowAwareText>

        <LoadingSpinner
          isVisible={isLoading}
          loadingIndicatorSize={15}
          sx={{ flex: 0, minWidth: '15px' }}
        />
      </Box>
    </MenuItem>
  );
}
